import { getIn } from "formik";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useTypedFormikContext } from "hooks/useTypedFormikContext";
import { ReactNode } from "react";
import { Section, SectionField } from "types/Models/Filing";
import { getSectionCompletion } from "util/booksHandoff/getSectionCompletion";
import { SubSectionCard } from "./SubSectionCard";

type SubSectionFieldsProps = {
  section: Section;
  radioValues: Record<string, unknown>;
  renderFields: (fields: SectionField[]) => ReactNode;
};

/** Books handoff layout: fields grouped into collapsible cards with completion counts */
export const SubSectionFields = ({
  section,
  radioValues,
  renderFields,
}: SubSectionFieldsProps) => {
  const { values, errors } = useTypedFormikContext<Record<string, unknown>>();
  const { isCustomer } = useRoleBasedView();

  const sectionFields = section.section_fields ?? [];
  const subSections = section.sub_sections ?? [];
  const fieldsOutsideSubSections = sectionFields.filter(
    (field) => !field.sub_section
  );

  const getCompletion = (fields: SectionField[]) =>
    getSectionCompletion({
      fields,
      values,
      radioValues,
      isCustomer: Boolean(isCustomer),
    });
  const hasErrors = (fields: SectionField[]) =>
    fields.some((field) => Boolean(getIn(errors, field.field_key)));
  const { answered, total } = getCompletion(sectionFields);

  const cards = subSections.map(({ key, title }) => {
    const fields = sectionFields.filter((field) => field.sub_section === key);
    const completion = getCompletion(fields);

    return (
      <SubSectionCard
        key={key}
        title={title}
        answered={completion.answered}
        total={completion.total}
        hasErrors={hasErrors(fields)}
      >
        {renderFields(fields)}
      </SubSectionCard>
    );
  });

  return (
    <>
      <div className="t-text-body-sm t-text-text-30 t-ml-2 t-mb-4">
        {answered}/{total} details completed
      </div>
      {cards}
      {renderFields(fieldsOutsideSubSections)}
    </>
  );
};
